/**
 * Stable identities for the destructible-world graph.
 *
 * Identity allocation is deliberately caller-owned: an ordinal is part of the
 * input, rather than being obtained from a module-global counter.  Replaying
 * the same scope, generation, and ordinals therefore produces byte-for-byte
 * equal keys, while a recycled scope gets a different generation key.
 */

import { createIdentitySystem, sameIdentity } from 'box3d-web';

export type IdentityKind = 'world' | 'structure' | 'element' | 'layer' | 'cell' | 'fragment';

declare const identityBrand: unique symbol;

export type DestructionIdentity<K extends IdentityKind = IdentityKind> = Readonly<{
  readonly kind: K;
  readonly namespace: string;
  readonly generation: number;
  readonly ordinal: number;
  readonly key: string;
  readonly parent?: DestructionIdentity;
}> & { readonly [identityBrand]: K };

export type WorldIdentity = DestructionIdentity<'world'>;
export type StructureIdentity = DestructionIdentity<'structure'>;
export type ElementIdentity = DestructionIdentity<'element'>;
export type LayerIdentity = DestructionIdentity<'layer'>;
export type CellIdentity = DestructionIdentity<'cell'>;
export type FragmentIdentity = DestructionIdentity<'fragment'>;

export interface IdentityScope {
  readonly namespace: string;
  readonly generation: number;
}

const IDENTITY_KINDS = ['world', 'structure', 'element', 'layer', 'cell', 'fragment'] as const satisfies readonly IdentityKind[];

const destructionIdentities = createIdentitySystem({
  kinds: IDENTITY_KINDS,
  parentOf: {
    world: null,
    structure: 'world',
    element: 'structure',
    layer: 'element',
    cell: 'layer',
    fragment: ['cell', 'layer'],
  },
});

/** Build one deterministic identity without touching shared mutable state. */
export function createDestructionIdentity<K extends IdentityKind>(
  kind: K,
  scope: IdentityScope,
  ordinal: number,
  parent?: DestructionIdentity,
): DestructionIdentity<K> {
  return destructionIdentities.forScope(scope).mint(kind, ordinal, parent as never) as unknown as DestructionIdentity<K>;
}

/**
 * Typed helpers for constructing the hierarchy.  This is a stateless factory:
 * callers choose ordinals from their own serialized definition or allocator.
 */
export function createDestructionIdentityFactory(scope: IdentityScope) {
  const scoped = destructionIdentities.forScope(scope);
  return Object.freeze({
    scope: scoped.scope,
    world: (ordinal: number): WorldIdentity =>
      scoped.mint('world', ordinal) as unknown as WorldIdentity,
    structure: (ordinal: number, world: WorldIdentity): StructureIdentity =>
      scoped.mint('structure', ordinal, world as never) as unknown as StructureIdentity,
    element: (ordinal: number, structure: StructureIdentity): ElementIdentity =>
      scoped.mint('element', ordinal, structure as never) as unknown as ElementIdentity,
    layer: (ordinal: number, element: ElementIdentity): LayerIdentity =>
      scoped.mint('layer', ordinal, element as never) as unknown as LayerIdentity,
    cell: (ordinal: number, layer: LayerIdentity): CellIdentity =>
      scoped.mint('cell', ordinal, layer as never) as unknown as CellIdentity,
    fragment: (ordinal: number, parent: CellIdentity | LayerIdentity): FragmentIdentity =>
      scoped.mint('fragment', ordinal, parent as never) as unknown as FragmentIdentity,
  });
}

export function sameDestructionIdentity(
  left: DestructionIdentity | undefined,
  right: DestructionIdentity | undefined,
): boolean {
  return sameIdentity(left as never, right as never);
}
